import { useState } from 'react';
import {
  buildStaticHeroImageUrls,
  generateHeroSrcSet,
  getHeroFallbackUrl,
} from '@/lib/responsive-hero-image';

interface ResponsiveHeroImageProps {
  baseName: string; // Base name like "hero-lagoon", "food-hall", "aerial-1"
  originalSrc: string; // Original image path like "/img/hero-lagoon.jpg" (fallback)
  alt: string;
  className?: string;
  loading?: 'lazy' | 'eager';
}

export function ResponsiveHeroImage({
  baseName,
  originalSrc,
  alt,
  className,
  loading = 'eager',
}: ResponsiveHeroImageProps) {
  const [error, setError] = useState(false);

  // Build image URLs from baseName
  const imageUrls = buildStaticHeroImageUrls(baseName);

  // Generate srcset strings for each format
  const avifSrcSet = generateHeroSrcSet(imageUrls, 'avif');
  const webpSrcSet = generateHeroSrcSet(imageUrls, 'webp');
  const jpegSrcSet = generateHeroSrcSet(imageUrls, 'jpeg');

  // Get fallback URL
  const fallbackUrl = getHeroFallbackUrl(imageUrls, baseName, originalSrc);

  // If error occurred or no srcsets generated, fallback to original
  if (error || (!avifSrcSet && !webpSrcSet && !jpegSrcSet)) {
    return (
      <img
        src={originalSrc}
        alt={alt}
        className={className}
        loading={loading}
        decoding="async"
        onError={() => setError(true)}
      />
    );
  }

  return (
    <picture>
      {/* AVIF - best compression, modern browsers */}
      {avifSrcSet && (
        <source
          type="image/avif"
          srcSet={avifSrcSet}
          sizes="100vw"
        />
      )}
      {/* WebP - good compression, wide support */}
      {webpSrcSet && (
        <source
          type="image/webp"
          srcSet={webpSrcSet}
          sizes="100vw"
        />
      )}
      {/* JPEG - fallback for older browsers */}
      <img
        src={fallbackUrl}
        srcSet={jpegSrcSet || undefined}
        sizes="100vw"
        alt={alt}
        className={className}
        loading={loading}
        decoding="async"
        onError={() => setError(true)}
      />
    </picture>
  );
}
